import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow, isAfter, subDays, format } from "date-fns";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";

function formatFieldName(field: string): string {
  return field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, s => s.toUpperCase())
    .replace(/_/g, " ");
}

function timeAgo(dt: string): string {
  const d = new Date(dt);
  if (isAfter(d, subDays(new Date(), 1))) return formatDistanceToNow(d, { addSuffix: true });
  return format(d, "MMM d, yyyy HH:mm");
}

function ActionDot({ action }: { action: string }) {
  const colors: Record<string, string> = {
    created:      "bg-green-500",
    updated:      "bg-blue-500",
    deleted:      "bg-red-500",
    enabled:      "bg-teal-500",
    disabled:     "bg-amber-500",
    status_changed: "bg-purple-500",
    activated:    "bg-teal-500",
    deactivated:  "bg-gray-400",
    adjusted:     "bg-amber-500",
    signoff_requested: "bg-indigo-500",
    signoff_approved:  "bg-green-600",
    signoff_rejected:  "bg-red-600",
    signoff_forwarded: "bg-indigo-400",
    signoff_cancelled: "bg-gray-500",
  };
  return <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${colors[action] ?? "bg-gray-400"}`} />;
}

function ActionBadge({ action, t }: { action: string; t: (key: string) => string }) {
  const map: Record<string, { cls: string; labelKey: string }> = {
    created:      { cls: "bg-green-100 text-green-800 border-green-200",   labelKey: "action_created" },
    updated:      { cls: "bg-blue-100 text-blue-800 border-blue-200",      labelKey: "action_updated" },
    deleted:      { cls: "bg-red-100 text-red-800 border-red-200",         labelKey: "action_deleted" },
    enabled:      { cls: "bg-teal-100 text-teal-800 border-teal-200",      labelKey: "action_enabled" },
    disabled:     { cls: "bg-amber-100 text-amber-800 border-amber-200",   labelKey: "action_disabled" },
    status_changed: { cls: "bg-purple-100 text-purple-800 border-purple-200", labelKey: "action_status_changed" },
    activated:    { cls: "bg-teal-100 text-teal-800 border-teal-200",      labelKey: "action_activated" },
    deactivated:  { cls: "bg-gray-100 text-gray-800 border-gray-200",      labelKey: "action_deactivated" },
    adjusted:     { cls: "bg-amber-100 text-amber-800 border-amber-200",   labelKey: "action_adjusted" },
    signoff_requested: { cls: "bg-indigo-100 text-indigo-800 border-indigo-200", labelKey: "action_signoff_requested" },
    signoff_approved:  { cls: "bg-green-100 text-green-800 border-green-200",    labelKey: "action_signoff_approved" },
    signoff_rejected:  { cls: "bg-red-100 text-red-800 border-red-200",          labelKey: "action_signoff_rejected" },
    signoff_forwarded: { cls: "bg-indigo-100 text-indigo-800 border-indigo-200", labelKey: "action_signoff_forwarded" },
    signoff_cancelled: { cls: "bg-gray-100 text-gray-800 border-gray-200",       labelKey: "action_signoff_cancelled" },
  };
  const m = map[action];
  const cls = m?.cls ?? "bg-gray-100 text-gray-800 border-gray-200";
  const label = m ? t(`notifMisc.${m.labelKey}`) : action;
  return (
    <Badge variant="outline" className={`uppercase text-[10px] font-bold border ${cls}`}>
      {label}
    </Badge>
  );
}

interface EntityHistoryProps {
  entityType: string;
  entityId: string | undefined | null;
  workspaceId?: string;
  limit?: number;
}

export function EntityHistory({ entityType, entityId, workspaceId, limit = 20 }: EntityHistoryProps) {
  const { t } = useTranslation();
  const { user, roles, currentWorkspace } = useAuth();
  const canView = hasPermission(user, "audit_trail.view" as any, roles);
  const wsId = workspaceId ?? currentWorkspace?.id;

  const safeId = entityId ?? "";
  const { data, isLoading } = useQuery({
    queryKey: ["audit-entity", entityType, safeId, wsId],
    queryFn: () => api.auditLogs.byEntity(wsId!, entityType, safeId, { limit }),
    enabled: !!wsId && !!safeId && canView,
  });

  if (!canView || !wsId || !entityId) return null;
  if (isLoading) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mt-4" />;
  if (!data?.logs?.length) return null;

  const logs: any[] = data.logs;

  return (
    <div className="mt-6 pt-6 border-t border-border">
      <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground mb-3">
        {t('notifMisc.changeHistory')}
      </h3>
      <div className="space-y-0">
        {logs.map((log, idx) => (
          <div key={log.id} className="flex items-start gap-3 text-sm">
            <div className="flex flex-col items-center">
              <ActionDot action={log.action} />
              {idx < logs.length - 1 && <div className="w-px flex-1 bg-border mt-1 min-h-[16px]" />}
            </div>
            <div className="flex-1 pb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{log.userName || t('notifMisc.system')}</span>
                <ActionBadge action={log.action} t={t} />
                <span className="text-xs text-muted-foreground font-mono">{timeAgo(log.createdAt)}</span>
              </div>
              {log.changes && log.changes.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {log.changes.map((c: any, i: number) => (
                    <div key={i} className="text-xs text-muted-foreground">
                      {formatFieldName(c.field)}:{" "}
                      {(c.oldValue ?? c.from) != null && (
                        <>
                          <span className="line-through text-red-600/70">{c.oldValue ?? c.from}</span>
                          <span className="mx-1">→</span>
                        </>
                      )}
                      <span className="text-green-700 dark:text-green-400">{c.newValue ?? c.to ?? "—"}</span>
                    </div>
                  ))}
                </div>
              )}
              {log.metadata && typeof log.metadata === "object" && (log.metadata.correctionType || log.metadata.correctionId) && (
                <div className="mt-1.5 rounded border bg-muted/30 p-2 text-xs space-y-1">
                  <p className="font-medium text-foreground">
                    {log.metadata.correctionType === "direct_post_confirmation" ? "Direct post-confirmation correction" : "Approved receipt correction"}
                  </p>
                  {log.metadata.reason && <p><span className="font-medium">Reason:</span> {String(log.metadata.reason)}</p>}
                  {Array.isArray(log.metadata.lineChanges) && log.metadata.lineChanges.map((change: any, i: number) => (
                    <p key={i}>
                      {change.itemName || change.itemId || "Receipt line"}: qty {String(change.qtyFrom)} → {String(change.qtyTo)}, cost {String(change.costFrom)} → {String(change.costTo)}
                    </p>
                  ))}
                </div>
              )}
              {(!log.changes || log.changes.length === 0) && log.metadata && typeof log.metadata === "object" && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {Object.entries(log.metadata)
                    .filter(([, v]) => v != null)
                    .slice(0, 3)
                    .map(([k, v]) => `${formatFieldName(k)}: ${v}`)
                    .join(" · ")}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
